import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Formik, FormikErrors } from 'formik';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { axiosKK } from '../../services/networkRequest';
import errorMessage from '../../services/errorMessage';
import SubmitButton from '../SubmitButton';
import Button from '../Button';
import Label from '../Label';
import Checkbox from '../Checkbox';

export type AdminPartnerType = 'COURIER' | 'WAREHOUSE' | 'BOTH';

export interface AdminPartner {
  id: number;
  name: string;
  type: AdminPartnerType;
  billingRate: number;
  contactEmail?: string | null;
  contactPhone?: string | null;
  address?: string | null;
  isActive: boolean;
  createdAt: number;
  updatedAt: number;
  deletedAt?: number | null;
}

interface CreatePartnerResponse {
  partner: AdminPartner;
}

interface Props {
  show: boolean;
  close: () => void;
  onSuccess: (partner: AdminPartner) => void;
}

interface FormValues {
  name: string;
  type: AdminPartnerType | '';
  billingRatePounds: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  isActive: boolean;
}

const TYPE_OPTIONS: { value: AdminPartnerType; label: string; description: string }[] = [
  { value: 'COURIER', label: 'Courier', description: 'Handles collections and deliveries' },
  { value: 'WAREHOUSE', label: 'Warehouse', description: 'Operates warehouse storage' },
  { value: 'BOTH', label: 'Both', description: 'Both courier and warehouse operations' },
];

function validate(values: FormValues): FormikErrors<FormValues> {
  const errors: FormikErrors<FormValues> = {};
  if (!values.name.trim()) {
    errors.name = 'Please enter a partner name.';
  } else if (values.name.trim().length > 255) {
    errors.name = 'Partner name is too long.';
  }
  if (!values.type) errors.type = 'Please choose a partner type.';

  const trimmedRate = values.billingRatePounds.trim();
  if (trimmedRate) {
    const n = Number(trimmedRate);
    if (Number.isNaN(n) || n < 0) {
      errors.billingRatePounds = 'Billing rate must be £0 or more.';
    }
  }

  if (values.contactEmail.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.contactEmail.trim())) {
    errors.contactEmail = "That email address doesn't look right.";
  }
  if (values.contactPhone.trim() && values.contactPhone.trim().length < 4) {
    errors.contactPhone = "That phone number doesn't look right.";
  }
  if (values.address.trim().length > 1000) {
    errors.address = 'That address is too long.';
  }
  return errors;
}

export default function CreatePartnerModal({ show, close, onSuccess }: Props) {
  const [networkError, setNetworkError] = React.useState<string>();

  useEffect(() => {
    if (!show) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [show, close]);

  if (!show) return null;

  const initialValues: FormValues = {
    name: '',
    type: '',
    billingRatePounds: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    isActive: true,
  };

  return ReactDOM.createPortal(
    <div className="logistics-modal__overlay" onClick={close}>
      <div
        className="logistics-modal__container"
        onClick={(e) => e.stopPropagation()}
      >
        <Formik
          initialValues={initialValues}
          validate={validate}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            setNetworkError(undefined);
            const billingRatePounds = Number(values.billingRatePounds.trim() || '0');
            const billingRatePence = Math.round(billingRatePounds * 100);

            const payload: Record<string, unknown> = {
              name: values.name.trim(),
              type: values.type,
              billingRate: billingRatePence,
              isActive: values.isActive,
            };
            if (values.contactEmail.trim()) payload.contactEmail = values.contactEmail.trim();
            if (values.contactPhone.trim()) payload.contactPhone = values.contactPhone.trim();
            if (values.address.trim()) payload.address = values.address.trim();

            axiosKK
              .post<CreatePartnerResponse>('/admin/partners', payload)
              .then(({ data }) => {
                onSuccess(data.partner);
                resetForm();
                close();
              })
              .catch((error) => {
                errorMessage(setNetworkError)(error);
              })
              .finally(() => {
                setSubmitting(false);
              });
          }}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            handleChange,
            handleBlur,
            handleSubmit,
            isValid,
            dirty,
          }) => (
            <form
              onSubmit={handleSubmit}
              className="logistics-modal__form"
              name="create-partner-form"
            >
              <div className="logistics-modal__header">
                <div className="logistics-modal__header-info">
                  <h3>Create Partner</h3>
                  <span className="logistics-modal__subtitle">
                    Set up a logistics partner organisation
                  </span>
                </div>
                <Button
                  variant="tertiary"
                  size="sm"
                  iconOnly
                  className="logistics-modal__close"
                  aria-label="Close"
                  onClick={close}
                >
                  <XMarkIcon aria-hidden="true" />
                </Button>
              </div>

              <div className="logistics-modal__body">
                <div className="logistics-modal__section">
                  <div className="logistics-modal__field">
                    <Label htmlFor="partner-name">Partner name</Label>
                    <input
                      className="logistics-modal__input"
                      type="text"
                      id="partner-name"
                      name="name"
                      autoComplete="off"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.name && errors.name && (
                      <span className="error">{errors.name}</span>
                    )}
                  </div>

                  <div className="logistics-modal__field">
                    <Label htmlFor="partner-type">Type</Label>
                    <select
                      className="logistics-modal__select admin-select"
                      id="partner-type"
                      name="type"
                      value={values.type}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <option value="">— Select type —</option>
                      {TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label} — {opt.description}
                        </option>
                      ))}
                    </select>
                    {touched.type && errors.type && (
                      <span className="error">{errors.type}</span>
                    )}
                  </div>

                  <div className="logistics-modal__field">
                    <Label htmlFor="partner-billing-rate">
                      Billing rate (£ per box per day)
                    </Label>
                    <input
                      className="logistics-modal__input"
                      type="number"
                      id="partner-billing-rate"
                      name="billingRatePounds"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={values.billingRatePounds}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.billingRatePounds && errors.billingRatePounds && (
                      <span className="error">{errors.billingRatePounds}</span>
                    )}
                  </div>
                </div>

                <div className="logistics-modal__section">
                  <div className="logistics-modal__field">
                    <Label htmlFor="partner-contact-email">
                      Contact email (optional)
                    </Label>
                    <input
                      className="logistics-modal__input"
                      type="email"
                      id="partner-contact-email"
                      name="contactEmail"
                      autoComplete="off"
                      value={values.contactEmail}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.contactEmail && errors.contactEmail && (
                      <span className="error">{errors.contactEmail}</span>
                    )}
                  </div>

                  <div className="logistics-modal__field">
                    <Label htmlFor="partner-contact-phone">
                      Contact phone (optional)
                    </Label>
                    <input
                      className="logistics-modal__input"
                      type="tel"
                      id="partner-contact-phone"
                      name="contactPhone"
                      value={values.contactPhone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.contactPhone && errors.contactPhone && (
                      <span className="error">{errors.contactPhone}</span>
                    )}
                  </div>

                  <div className="logistics-modal__field">
                    <Label htmlFor="partner-address">Address (optional)</Label>
                    <textarea
                      className="logistics-modal__input"
                      id="partner-address"
                      name="address"
                      rows={3}
                      value={values.address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.address && errors.address && (
                      <span className="error">{errors.address}</span>
                    )}
                  </div>
                </div>

                <div className="logistics-modal__section">
                  <Checkbox
                    name="isActive"
                    checked={values.isActive}
                    onChange={handleChange}
                    label="Active immediately"
                  />
                </div>

                {networkError && <span className="error">{networkError}</span>}
              </div>

              <div className="logistics-modal__footer">
                <SubmitButton
                  disabled={!isValid || !dirty}
                  isSubmitting={isSubmitting}
                >
                  Create Partner
                </SubmitButton>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>,
    document.body
  );
}
